import React, { useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Bell, User, CreditCard, LogOut, X, ChevronRight, Wallet } from "lucide-react";
import { JobCenterProvider } from "../../features/jobsV5/JobCenterContext";
import { useAuth } from "../../contexts/AuthContext";
import BottomNav from "./BottomNav";
import CommandPalette from "./CommandPalette";
import FavoriteToggle from "./FavoriteToggle";
import logo from "../../assets/Logo CLU I White.png";
import BugReportButton from "../common/BugReportButton";
import PlatformNoticeBar from "../common/PlatformNoticeBar";
import { displayName as resolveDisplayName } from '../../utils/displayName';
import { useMyHandle } from '../../hooks/useMyHandle';

const SELF_SCROLLING_ROUTES = ["/m/clu-ng", "/m/autodemo", "/m/galaxia"];

const MobileLayout: React.FC = () => {
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userInfo, logout } = useAuth();

  const isSelfScrolling = SELF_SCROLLING_ROUTES.some((r) =>
    location.pathname.startsWith(r)
  );

  const myHandle = useMyHandle();
  const displayName: string = resolveDisplayName({
    name: userInfo?.user?.name || currentUser?.displayName,
    email: userInfo?.user?.email || currentUser?.email,
    handle: myHandle,
  });

  const email: string =
    userInfo?.user?.email || currentUser?.email || "";

  const initials: string = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    await logout();
  };

  return (
    <JobCenterProvider>
      <div className="mobile-layout">
        <header
          className="mobile-layout-header"
          style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}
        >
          <div className="mobile-layout-header-inner">
            <button
              type="button"
              className="mobile-layout-logo-btn"
              onClick={() => navigate("/m/clu-ng")}
              aria-label="Go to CLU"
            >
              <img src={logo} alt="CLU" className="mobile-layout-logo" />
              <span className="mobile-layout-logo-label">CLU</span>
            </button>

            <div className="mobile-layout-header-actions">
              <FavoriteToggle />
              <button
                type="button"
                className="mobile-layout-icon-btn"
                onClick={() => setIsUserMenuOpen(true)}
                aria-label="User menu"
              >
                <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                  {initials}
                </div>
              </button>
            </div>
          </div>
        </header>

        <PlatformNoticeBar />

        <main className="mobile-layout-content">
          {isSelfScrolling ? (
            <Outlet />
          ) : (
            <div className="mobile-page-scroll">
              <Outlet />
            </div>
          )}
        </main>

        <BottomNav onOpenCommandPalette={() => setIsPaletteOpen(true)} />

        <CommandPalette
          isOpen={isPaletteOpen}
          onClose={() => setIsPaletteOpen(false)}
        />

        {isUserMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/40 z-40"
              onClick={() => setIsUserMenuOpen(false)}
            />
            <div className="fixed bottom-0 inset-x-0 z-50 bg-surface rounded-t-2xl shadow-2xl pb-bottom-sheet">
              <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-line">
                <div>
                  <p className="text-sm font-semibold text-ink">{displayName}</p>
                  <p className="text-xs text-ink-subtle truncate max-w-[220px]">{email}</p>
                </div>
                <button
                  onClick={() => setIsUserMenuOpen(false)}
                  className="p-1.5 rounded-lg text-ink-subtle hover:bg-surface-2"
                >
                  <X size={18} />
                </button>
              </div>

              <nav className="py-2">
                <Link
                  to="/m/notifications"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 min-h-[52px] hover:bg-surface-2 transition-colors touch-manipulation"
                >
                  <Bell size={18} className="text-ink-subtle" />
                  <span className="text-sm text-ink flex-1">Notifications</span>
                  <ChevronRight size={16} className="text-gray-300" />
                </Link>

                <Link
                  to="/m/profile"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 min-h-[52px] hover:bg-surface-2 transition-colors touch-manipulation"
                >
                  <User size={18} className="text-ink-subtle" />
                  <span className="text-sm text-ink flex-1">My Profile</span>
                  <ChevronRight size={16} className="text-gray-300" />
                </Link>

                <Link
                  to="/m/billing"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 min-h-[52px] hover:bg-surface-2 transition-colors touch-manipulation"
                >
                  <CreditCard size={18} className="text-ink-subtle" />
                  <span className="text-sm text-ink flex-1">Billing</span>
                  <ChevronRight size={16} className="text-gray-300" />
                </Link>

                <Link
                  to="/m/my-wallet"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3.5 min-h-[52px] hover:bg-surface-2 transition-colors touch-manipulation"
                >
                  <Wallet size={18} className="text-ink-subtle" />
                  <span className="text-sm text-ink flex-1">My Wallet</span>
                  <ChevronRight size={16} className="text-gray-300" />
                </Link>

                <div className="my-1 mx-4 border-t border-line" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3.5 min-h-[52px] hover:bg-red-50 hover:dark:bg-red-900/30 transition-colors touch-manipulation text-left"
                >
                  <LogOut size={18} className="text-red-400 dark:text-red-500 dark:text-red-400" />
                  <span className="text-sm text-red-500 dark:text-red-400">Log Out</span>
                </button>
              </nav>
            </div>
          </>
        )}

        <BugReportButton placement="mobile" />
      </div>
    </JobCenterProvider>
  );
};

export default MobileLayout;
